import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { ACTION_LABELS, actionColor } from './constants';
import Pagination from '../../components/ui/Pagination';
import FilterTabs from '../../components/ui/FilterTabs';
import { ChevronDownIcon } from '../../components/ui/Icons';
import { EmptyState } from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

export default function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      if (actionFilter) params.set('action', actionFilter);
      if (search) params.set('search', search);
      const res = await api.get(`/audit?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setActions(res.data.actions);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const allFilters = [
    { id: '', label: 'All' },
    ...actions.map(a => ({ id: a, label: ACTION_LABELS[a] || a })),
  ];

  return (
    <div className="space-y-4">
      <div className="card p-4 space-y-3">
        <div className="relative">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round"
               className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            className="input pl-10"
            placeholder="Search by actor, target, or action…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <FilterTabs
          filters={allFilters}
          current={actionFilter}
          onChange={f => { setActionFilter(f); setPage(1); }}
        />
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-12 flex justify-center"><LoadingSpinner /></div>
        ) : logs.length === 0 ? (
          <EmptyState
            icon={<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></>}
            title="No log entries found."
          />
        ) : (
          <div className="divide-y divide-white/[0.05]">
            {logs.map(log => (
              <div key={log.id} className="p-4 hover:bg-white/[0.02] transition-colors">
                <div
                  className="flex items-start justify-between gap-4 cursor-pointer"
                  onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold
                                      whitespace-nowrap flex-shrink-0 ${actionColor(log.action)}`}>
                      {ACTION_LABELS[log.action] || log.action}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-300">
                        <span className="font-medium text-white">{log.actor_name || 'system'}</span>
                        {log.target_name && (
                          <>
                            <span className="text-gray-600 mx-1.5">→</span>
                            <span className="font-medium text-gray-200">{log.target_name}</span>
                          </>
                        )}
                      </p>
                      <p className="text-xs text-gray-600 mt-1 font-mono">
                        {log.created_at?.slice(0, 16).replace('T', ' ')}
                        <span className="mx-1.5 text-gray-700">·</span>
                        IP: {log.ip_address || 'unknown'}
                      </p>
                    </div>
                  </div>
                  <ChevronDownIcon className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform duration-300 ${expanded === log.id ? 'rotate-180' : ''}`} />
                </div>

                {expanded === log.id && log.details && Object.keys(log.details).length > 0 && (
                  <div className="mt-3 surface-sunken p-3.5">
                    <pre className="text-xs text-gray-400 font-mono whitespace-pre-wrap break-all leading-relaxed">
                      {JSON.stringify(log.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Pagination page={page} totalPages={Math.ceil(total / 25)} total={total} onPage={setPage} />
    </div>
  );
}
